import camelcaseKeys from 'camelcase-keys';
import FuzzySearch from 'fuzzy-search';
import { combineReducers } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { portalApi } from '../../../api';
import {
  getEnhancedPolicyData,
  getMinimumInsuranceRequirements
} from '../../../components/CertificateCreationFlow/util';
import DataTypes from '../../../constants/dataTypes';
import { FailedGptRequestStatuses, GptRequestStatuses } from '../../../constants/gptRequestStatuses';
import { AdditionalPolicyTypeNames, PolicyTypes } from '../../../constants/policyTypes';
import { Statuses } from '../../../constants/statuses';
import { usePrepareDocument } from '../../../services/document-service';
import insuredKeys from '../../../services/models/insured/keys';
import recordKeys from '../../../services/models/record/keys';
import { createMap, modifyArrayItemAt, removeArrayItemAt, removeArrayItemAtIndex } from '../../../utils/arrayUtil';
import { isValidDateString } from '../../../utils/dateUtil';
import { debounceAction } from '../../../utils/debounce';
import { queryClient } from '../../../utils/queryClientUtil';
import { createErrorToast, createSuccessToast, createToast } from '../../modules/app';
import { v2MatchPoliciesToRequirements } from '../../modules/insured';
import { hideTray } from '../../modules/tray';

import {
  setAutoPolicyData,
  setGlPolicyData,
  setOtherPolicyData,
  setUmbExcPolicyData,
  setWorkersCompPolicyData
} from './util';

export const withCertificateBlueprint = (name, baseReducer, stateOverrides = {}) => {
  const CERTS_PREPARE_DOCUMENT = 'CERTS_PREPARE_DOCUMENT';
  const CERTS_PREPARE_DOCUMENT_SUCCESS = 'CERTS_PREPARE_DOCUMENT_SUCCESS';
  const CERTS_PREPARE_DOCUMENT_FAILURE = 'CERTS_PREPARE_DOCUMENT_FAILURE';
  const CERTS_OCR_DOCUMENT = 'CERTS_OCR_DOCUMENT';
  const CERTS_OCR_DOCUMENT_SUCCESS = 'CERTS_OCR_DOCUMENT_SUCCESS';
  const CERTS_SCAN_DOCUMENT = 'CERTS_OCR_DOCUMENT';
  const CERTS_SCAN_DOCUMENT_SUCCESS = 'CERTS_OCR_DOCUMENT_SUCCESS';
  const CERTS_SCAN_DOCUMENT_FAILURE = 'CERTS_OCR_DOCUMENT_FAILURE';
  const CERTS_AI_SCAN_DOCUMENT = 'CERTS_AI_SCAN_DOCUMENT';
  const CERTS_AI_SCAN_DOCUMENT_SUCCESS = 'CERTS_AI_SCAN_DOCUMENT';
  const CERTS_EXTRACT_POLICY_DATA_FROM_OPENAI_RESPONSE = 'CERTS_EXTRACT_POLICY_DATA_FROM_OPENAI_RESPONSE';
  const CERTS_POLL_OCR_DOCUMENT_STATUS = 'CERTS_POLL_OCR_DOCUMENT_STATUS';
  const CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE = 'CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE';
  const CERTS_SET_OCR_IS_CANCELED = 'CERTS_SET_OCR_IS_CANCELED';
  const CERTS_REMOVE_DOCUMENT = 'CERTS_REMOVE_DOCUMENT';
  const CERTS_SET_CURRENT_FILE = 'CERTS_SET_CURRENT_FILE';
  const CERTS_SET_CURRENT_STEP = 'CERTS_SET_CURRENT_STEP';
  const CERTS_SET_FIRST_STEP = 'CERTS_SET_FIRST_STEP';
  const CERTS_SET_JOB = 'CERTS_SET_JOB';
  const CERTS_SET_DEPARTMENT = 'CERTS_SET_DEPARTMENT';
  const CERTS_SET_COMPANY = 'CERTS_SET_COMPANY';
  const CERTS_SET_AGENCY_ID = 'CERTS_SET_AGENCY_ID';
  const CERTS_SET_DOCUMENT_REQUIREMENTS = 'CERTS_SET_DOCUMENT_REQUIREMENTS';
  const CERTS_SET_LANGUAGE_REQUIREMENTS = 'CERTS_SET_LANGUAGE_REQUIREMENTS';
  const CERTS_SET_OPTIONS = 'CERTS_SET_OPTIONS';
  const CERTS_SET_INSURED_REQUIREMENTS = 'CERTS_SET_INSURED_REQUIREMENTS';
  const CERTS_SET_INSURED = 'CERTS_SET_INSURED';
  const CERTS_SET_INSURANCE_POLICY_TYPES = 'CERTS_SET_INSURANCE_POLICY_TYPES';
  const CERTS_SET_REQUIRED_POLICY_TYPES = 'CERTS_SET_REQUIRED_POLICY_TYPES';
  const CERTS_ADD_ADDITIONAL_INSURANCE_POLICY_TYPE = 'CERTS_ADD_ADDITIONAL_INSURANCE_POLICY_TYPE';
  const CERTS_REMOVE_ADDITIONAL_INSURANCE_POLICY_TYPE = 'CERTS_REMOVE_ADDITIONAL_INSURANCE_POLICY_TYPE';
  const CERTS_ADD_SELECTED_INSURANCE_POLICY_TYPE = 'CERTS_ADD_SELECTED_INSURANCE_POLICY_TYPE';
  const CERTS_REMOVE_SELECTED_INSURANCE_POLICY_TYPE = 'CERTS_REMOVE_SELECTED_INSURANCE_POLICY_TYPE';
  const CERTS_SET_ITEM_REQUIREMENTS = 'CERTS_SET_ITEM_REQUIREMENTS';
  const CERTS_SET_USER = 'CERTS_SET_USER';
  const CERTS_SET_POLICY_REVIEW_CONTENT_HEIGHT = 'CERTS_SET_POLICY_REVIEW_CONTENT_HEIGHT';
  const CERTS_SET_POLICY_DATA = 'CERTS_SET_POLICY_DATA';
  const CERTS_NEXT_POLICY_TYPE = 'CERTS_NEXT_POLICY_TYPE';
  const CERTS_SKIP_POLICY_TYPE = 'CERTS_SKIP_POLICY_TYPE';
  const CERTS_PREVIOUS_POLICY_TYPE = 'CERTS_PREVIOUS_POLICY_TYPE';
  const CERTS_LIST_INSURANCE_COMPANIES = 'CERTS_LIST_INSURANCE_COMPANIES';
  const CERTS_LIST_INSURANCE_COMPANIES_SUCCESS = 'CERTS_LIST_INSURANCE_COMPANIES_SUCCESS';
  const CERTS_LIST_INSURANCE_COMPANIES_FAILURE = 'CERTS_LIST_INSURANCE_COMPANIES_FAILURE';
  const CERTS_GET_DOCUMENT_TYPES = 'CERTS_GET_DOCUMENT_TYPES';
  const CERTS_GET_DOCUMENT_TYPES_SUCCESS = 'CERTS_GET_DOCUMENT_TYPES_SUCCESS';
  const CERTS_GET_DOCUMENT_TYPES_FAILURE = 'CERTS_GET_DOCUMENT_TYPES_FAILURE';
  const CERTS_GET_USER_DOCUMENTS = 'CERTS_GET_USER_DOCUMENTS';
  const CERTS_GET_USER_DOCUMENTS_SUCCESS = 'CERTS_GET_USER_DOCUMENTS_SUCCESS';
  const CERTS_GET_USER_DOCUMENTS_FAILURE = 'CERTS_GET_USER_DOCUMENTS_FAILURE';
  const CERTS_DELETE_DOCUMENT = 'CERTS_DELETE_DOCUMENT';
  const CERTS_DELETE_DOCUMENT_SUCCESS = 'CERTS_DELETE_DOCUMENT_SUCCESS';
  const CERTS_DELETE_DOCUMENT_FAILURE = 'CERTS_DELETE_DOCUMENT_FAILURE';
  const CERTS_GET_USER_POLICIES = 'CERTS_GET_USER_POLICIES';
  const CERTS_GET_USER_POLICIES_SUCCESS = 'CERTS_GET_USER_POLICIES_SUCCESS';
  const CERTS_GET_USER_POLICIES_FAILURE = 'CERTS_GET_USER_POLICIES_FAILURE';
  const CERTS_UPDATE_POLICY = 'CERTS_UPDATE_POLICY';
  const CERTS_UPDATE_POLICY_SUCCESS = 'CERTS_UPDATE_POLICY_SUCCESS';
  const CERTS_UPDATE_POLICY_FAILURE = 'CERTS_UPDATE_POLICY_FAILURE';
  const CERTS_RESET = 'CERTS_RESET';
  const CERTS_SUBMIT = 'CERTS_SUBMIT';
  const CERTS_SUBMIT_SUCCESS = 'CERTS_SUBMIT_SUCCESS';
  const CERTS_SUBMIT_FAILURE = 'CERTS_SUBMIT_FAILURE';

  const CERTS_CREATE_INSURANCE_COMPANY = 'CERTS_CREATE_INSURANCE_COMPANY';
  const CERTS_CREATE_INSURANCE_COMPANY_SUCCESS = 'CERTS_CREATE_INSURANCE_COMPANY_SUCCESS';
  const CERTS_CREATE_INSURANCE_COMPANY_FAILURE = 'CERTS_CREATE_INSURANCE_COMPANY_FAILURE';
  const CERTS_CREATE_INSURANCE_DOCUMENT_TYPE = 'CERTS_CREATE_INSURANCE_DOCUMENT_TYPE';
  const CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_SUCCESS = 'CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_SUCCESS';
  const CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_FAILURE = 'CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_FAILURE';

  const CERTS_SEND_SUBMISSION_EMAIL = 'CERTS_SEND_SUBMISSION_EMAIL';
  const CERTS_SET_INSURANCE_REQUIREMENT = 'CERTS_SET_INSURANCE_REQUIREMENT';

  const initialState = {
    gptRequestId: null,
    policyGptRequestId: null,
    aiScanning: false,
    aiScanResults: null,
    agencyId: null,
    isLoading: false,
    isSubmitting: false,
    loadingHeader: null,
    loadingSubText: null,
    preparingDocument: false,
    contentHeight: '100%',
    linkedDocuments: {},
    currentStep: 0,
    currentFileIndex: 0,
    currentPolicyTypeIndex: 0,
    policyData: {},
    documents: [],
    documentRequirements: [],
    userDocuments: [],
    userPolicies: [],
    policiesLoading: true,
    documentsLoading: true,
    languageRequirements: [],
    minimumRequirements: {},
    itemRequirements: [],
    insuredRequirements: [],
    insuranceRequirement: null,
    requirementPolicyTypes: [],
    companyPolicyTypes: [],
    selectedPolicyTypes: [],
    selectedAdditionalPolicyTypes: [],
    insuranceDocumentTypes: [],
    policyReviewContentHeight: '64vh',
    insuranceCompanies: {
      pageData: {
        page: 1,
        pageSize: 100
      },
      data: []
    },
    submissionText: ' Click Submit Insurance below to notify the Certificate Holder of your submission.\n'
      + 'The Certificate Holder is immediately notified via email. They will reach out\n'
      + 'should they have any additional questions or concerns.',
    options: {
      ocr: false,
      aiScan: false,
      showCertHolderInformation: false,
      showInsuredInformation: false,
      showPreviousSubmissions: false,
      updateInsuredProfilePage: true,
      afterSubmitRedirectUrl: null,
      chooseAnyPolicyType: false,
      shouldSync: false,
      requireAgentDetails: false
    },
    user: {},
    ocr: {
      documents: {},
      isCanceled: false
    },
    ...stateOverrides
  };

  /* Actions
  ========================================================= */
  const prepareDocument = (file, checkedDocTypeIds, checkedPolicyTypeIds) => async (dispatch, getState) => {
    const { id: recordId } = selectInsuranceRequirement(getState());
    dispatch(createToast({
      title: 'Preparing Document...',
      description: file.size > 10 * 1024 * 1024 ? 'Files larger than 10MB may take longer to process' : null,
      duration: 60000
    }));

    dispatch({ name, type: CERTS_PREPARE_DOCUMENT });

    const fileUniqueId = uuidv4();

    try {
      const promises = checkedDocTypeIds.map(docTypeId => usePrepareDocument(
        recordId,
        docTypeId,
        file,
        dispatch,
        fileUniqueId
      ));

      const preparedDocuments = await Promise.all(promises);

      dispatch(createSuccessToast({ title: 'Document Prepared!', duration: 2000 }));

      dispatch({
        preparedDocuments,
        name,
        type: CERTS_PREPARE_DOCUMENT_SUCCESS,
        file,
        checkedDocTypeIds,
        checkedPolicyTypeIds,
        previewLink: URL.createObjectURL(file),
        camelCase: false
      });
    } catch (error) {
      dispatch(createErrorToast({ title: 'Uh oh, an error occurred', description: 'Your document was not prepared successfully', error }));

      dispatch({ name, type: CERTS_PREPARE_DOCUMENT_FAILURE, error });
    }
  };

  const aiScanDocuments = () => async (dispatch, getState) => {
    const { aiScan: aiFeatureEnabled } = selectOptions(getState());

    if (!aiFeatureEnabled) {
      console.log('AI Feature Not Enabled.  Skipping scan');
      return false; // ai feature not enabled
    }

    const { id: recordId } = selectInsuranceRequirement(getState());
    const documents = selectAllDocuments(getState());
    const docIds = documents.reduce((acc, doc) => {
      if (doc?.preparedDocuments?.length) {
        doc.preparedDocuments.forEach(preparedDoc => {
          acc.push(preparedDoc.id);
        });
      }

      return acc;
    }, []);

    if (!docIds.length) {
      return false; // no documents to scan
    }

    dispatch({ name, type: CERTS_AI_SCAN_DOCUMENT, docIds, recordId });

    try {
      const response = await portalApi.aiScanDocuments({ recordId, docIds, sync: false, publishAnswers: false });

      dispatch({ name, type: CERTS_AI_SCAN_DOCUMENT_SUCCESS, response });
    } catch (error) {
      dispatch({
        name,
        type: CERTS_SCAN_DOCUMENT_FAILURE,
        error
      });
    }

    return true;
  };

  const extractPolicyDataFromOpenAiResponse = response => async (dispatch, getState) => {
    dispatch({
      name,
      type: CERTS_EXTRACT_POLICY_DATA_FROM_OPENAI_RESPONSE,
      response
    });

    const companyId = selectCompany(getState())?.companyId;
    const companyPolicyTypes = selectCompanyPolicyTypes(getState());
    let policyRequirements = selectRequirementPolicyTypes(getState());
    const selectedAdditionalPolicyTypes = selectSelectedAdditionalPolicyTypes(getState());

    policyRequirements = [
      ...policyRequirements,
      ...selectedAdditionalPolicyTypes
    ];

    const glType = companyPolicyTypes.find(policyType => policyType.name === PolicyTypes.General);
    const autoType = companyPolicyTypes.find(policyType => policyType.name === PolicyTypes.Auto);
    const umbType = companyPolicyTypes.find(policyType => policyType.name === PolicyTypes.UmbrellaExcess);
    const workersType = companyPolicyTypes.find(policyType => policyType.name === PolicyTypes.WorkersComp);

    const glRequirements = policyRequirements.find(policyType => policyType.name === PolicyTypes.General);
    const autoRequirements = policyRequirements.find(policyType => policyType.name === PolicyTypes.Auto);
    const umbRequirements = policyRequirements.find(policyType => policyType.name === PolicyTypes.UmbrellaExcess);
    const workersRequirements = policyRequirements.find(policyType => policyType.name === PolicyTypes.WorkersComp);

    const glRequired = glRequirements ? !!Object.keys(glRequirements)?.length : false;
    const autoRequired = autoRequirements ? !!Object.keys(autoRequirements)?.length : false;
    const umbRequired = umbRequirements ? !!Object.keys(umbRequirements)?.length : false;
    const workersRequired = workersRequirements ? !!Object.keys(workersRequirements)?.length : false;

    // split response to remove the updates from the stream:
    const splitResponse = response.split('Beginning extraction...\n');

    const data = JSON.parse(splitResponse[1]);

    const insurerTable = data?.insurer_table;
    const insuranceCompaniesByLetter = {};

    await Promise.all(
      Object.entries(insurerTable).map(async ([key, value]) => {
        let insName = value?.insurer_name;
        let naic = value?.insurer_naic;

        // check if objects were returned (confidence checks) instead of direct values
        if (typeof insName === 'object') {
          insName = typeof insName?.value === 'string' ? insName?.value : null;
        }

        if (typeof naic === 'object') {
          naic = typeof naic?.value === 'string' ? naic?.value : null;
        }

        try {
          if (insName && naic) {
            insuranceCompaniesByLetter[key] = await portalApi.createInsuranceCompany(
              companyId,
              insName,
              naic
            );
          } else {
            insuranceCompaniesByLetter[key] = null;
          }
        } catch (e) {
          insuranceCompaniesByLetter[key] = null;
        }
      })
    );

    const {
      general_liability_policies: gLPolicies,
      auto_liability_policies: autoPolicies,
      umbrella_policies: umbrellaPolicies,
      workers_comp_policies: workersPolicies,
      other_policies: otherPolicies
    } = data;

    setGlPolicyData(
      gLPolicies,
      insuranceCompaniesByLetter,
      glType,
      glRequired,
      dispatch,
      setPolicyData,
      policyRequirements
    );

    setAutoPolicyData(
      autoPolicies,
      insuranceCompaniesByLetter,
      autoType,
      autoRequired,
      dispatch,
      setPolicyData,
      policyRequirements
    );

    setUmbExcPolicyData(
      umbrellaPolicies,
      insuranceCompaniesByLetter,
      umbType,
      umbRequired,
      dispatch,
      setPolicyData,
      policyRequirements
    );

    setWorkersCompPolicyData(
      workersPolicies,
      insuranceCompaniesByLetter,
      workersType,
      workersRequired,
      dispatch,
      setPolicyData,
      policyRequirements
    );

    const existingPolicyData = selectPolicyData(getState());

    setOtherPolicyData(
      otherPolicies,
      insuranceCompaniesByLetter,
      companyPolicyTypes,
      dispatch,
      setPolicyData,
      policyRequirements,
      existingPolicyData
    );

    dispatch(nextStep());

    return true;
  };

  const scanDocument = (docId, file) => async dispatch => {
    dispatch({
      name,
      type: CERTS_SCAN_DOCUMENT,
      file,
      docId
    });

    dispatch(setOcrIsCanceled(false));

    try {
      const startDocumentScan = await portalApi.startDocumentScan(docId);

      const { scan_job_id: scanJobId } = startDocumentScan;

      dispatch(pollDocumentScan(docId, scanJobId, file));
    } catch (error) {
      dispatch({
        name,
        type: CERTS_SCAN_DOCUMENT_FAILURE,
        error
      });
    }
  };

  const pollDocumentScan = (docId, scanJobId, file) => async (dispatch, getState) => {
    let count = 0;

    const intervalId = setInterval(async () => {
      const ocrCanceled = selectOcrIsCanceled(getState());

      if (ocrCanceled) {
        clearInterval(intervalId);
        dispatch({
          name,
          type: CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE,
          error: Error('Scan canceled by user'),
          docId,
          fileName: file.name
        });

        dispatch(createToast({ title: 'Data Extraction Skipped' }));
      }

      if (count > 20) {
        clearInterval(intervalId);

        dispatch({
          name,
          type: CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE,
          error: Error('Doc scan took too long'),
          docId,
          fileName: file.name
        });

        dispatch(createToast({
          title: 'Please manually enter your policy details.',
          status: 'warning',
          duration: 5000
        }));
      }

      try {
        const pollResponse = await portalApi.pollDocumentScan(docId, scanJobId);
        const { status } = pollResponse;

        if (status === 'SUCCEEDED') {
          clearInterval(intervalId);

          const extractedData = await portalApi.getDocumentScan(docId, scanJobId);

          await dispatch(mapExtractedDocScanData(extractedData));

          dispatch({
            name,
            type: CERTS_OCR_DOCUMENT_SUCCESS,
            extractedData,
            docId,
            file,
            timeToScan: `${count} seconds`,
            camelCase: false
          });
        }
      } catch (error) {
        clearInterval(intervalId);

        dispatch({
          name,
          type: CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE,
          error,
          docId,
          file
        });

        dispatch(createToast({
          title: 'Please manually enter your policy details.',
          status: 'warning',
          duration: 5000
        }));
      }

      count += 1;
    }, 4000);
  };

  const mapExtractedDocScanData = extractedData => async (dispatch, getState) => {
    const {
      general_liability,
      auto_liability,
      umbrella_excess_liability,
      workers_compensation,
      other
    } = extractedData;

    // extract general liability details
    dispatch(setCorePolicyTypeData(general_liability, 'general', ['general liability', 'general']));

    // extract auto liability details
    dispatch(setCorePolicyTypeData(auto_liability, 'auto', ['automobile liability', 'auto liability', 'auto']));

    // extract umbrella/excess details
    dispatch(setCorePolicyTypeData(umbrella_excess_liability, 'umbrella_excess', ['umbrella/excess', 'umbrella excess', 'umbrella', 'excess']));

    // extract workers comp details
    dispatch(setCorePolicyTypeData(workers_compensation, 'workers_comp', ['workers compensation', 'workers comp']));

    // extract custom details
    dispatch(setOtherPolicyTypeData(other));
  };

  const setCorePolicyTypeData = (policyData, policyTypeName, fuzzyMatchNames = []) => (dispatch, getState) => {
    if (!policyTypeNameIsCoreFour(policyTypeName)) {
      return false;
    }

    const companyPolicyTypes = selectCompanyPolicyTypes(getState());
    let policyRequirements = selectRequirementPolicyTypes(getState());
    const selectedAdditionalPolicyTypes = selectSelectedAdditionalPolicyTypes(getState());
    const selectedRequirements = selectSelectedPolicyTypes(getState());
    const currentPolicyData = selectPolicyData(getState());
    const { chooseAnyPolicyType } = selectOptions(getState());
    const corePolicyType = companyPolicyTypes.find(policyType => policyType.name === policyTypeName);

    policyRequirements = [
      ...policyRequirements,
      ...selectedAdditionalPolicyTypes
    ];

    if (corePolicyType.id in currentPolicyData) {
      console.log(`${policyTypeName} already exists, not setting ${policyTypeName} data`);

      return false;
    }

    const policyRequirementsCorePolicyType = policyRequirements.find(policyType => policyType.name === corePolicyType.name);
    const selectedRequirementsCorePolicyType = selectedRequirements.find(policyType => policyType.name === corePolicyType.name);

    const policyRequirementsHasCorePolicyType = policyRequirementsCorePolicyType ? !!Object.keys(policyRequirementsCorePolicyType).length : false;
    const selectedPolicyRequirementsHasCorePolicyType = selectedRequirementsCorePolicyType ? !!Object.keys(selectedRequirementsCorePolicyType).length : false;

    if (!policyRequirementsHasCorePolicyType && !selectedPolicyRequirementsHasCorePolicyType) {
      const policyTypeSearcher = new FuzzySearch(policyRequirements, ['displayName', 'name'], { sort: true });

      // add check for custom policy type
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < fuzzyMatchNames.length; i++) {
        const fuzzyMatchName = fuzzyMatchNames[i];

        const fuzzySearchOnPolicyRequirements = policyTypeSearcher.search(fuzzyMatchName);

        if (fuzzySearchOnPolicyRequirements.length) {
          const customPolicyRequirement = fuzzySearchOnPolicyRequirements[0];

          const customPolicyData = mapCustomCoreDataToProperties(policyTypeName, customPolicyRequirement, policyData, corePolicyType);

          const enhancedCorePolicyData = getEnhancedPolicyData(
            customPolicyRequirement,
            camelcaseKeys(customPolicyData?.insurance_company, { deep: true }),
            customPolicyData
          );

          dispatch(setPolicyData(customPolicyRequirement.id, enhancedCorePolicyData));

          console.log(`${policyTypeName} policy data should have been set`, enhancedCorePolicyData);

          return enhancedCorePolicyData;
        }
      }

      console.log(`No policyRequirementsHasCorePolicyType and no selectedPolicyRequirementsHasCorePolicyType
      for ${policyTypeName}, skipping`);

      return false;
    }

    if (chooseAnyPolicyType && !selectedPolicyRequirementsHasCorePolicyType) {
      console.log(`The policy type, ${policyTypeName}, has not been selected for submission, skipping`);

      return false;
    }

    const corePolicyData = mapCoreExtractedDataToProperties(corePolicyType, policyData);

    const enhancedCorePolicyData = getEnhancedPolicyData(
      corePolicyType,
      camelcaseKeys(corePolicyData?.insurance_company, { deep: true }),
      corePolicyData
    );

    dispatch(setPolicyData(corePolicyType.id, enhancedCorePolicyData));

    console.log(`${policyTypeName} policy data should have been set`);

    return enhancedCorePolicyData;
  };

  const mapCoreExtractedDataToProperties = (policyType, extractedData) => {
    const { name: policyTypeName, properties } = policyType;

    if (!policyTypeNameIsCoreFour(policyType.name)) {
      return false;
    }

    switch (policyType.name) {
      case 'general': {
        return mapGeneralProperties(extractedData, properties);
      }

      case 'auto': {
        return mapAutoProperties(extractedData, properties);
      }

      case 'umbrella_excess': {
        return mapUmbrellaExcessProperties(extractedData, properties);
      }

      case 'workers_comp': {
        return mapWorkCompProperties(extractedData, properties);
      }

      default:
        throw new Error(`Something went wrong extracting the policy details for ${policyTypeName}`);
    }
  };

  const mapGeneralProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      comm,
      claims_made,
      occur,
      gen_agg_project,
      gen_agg_policy,
      gen_agg_loc,
      gen_agg_other,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_each_occur,
      limits_damage,
      limits_med_exp,
      limits_injury,
      limits_gen_agg,
      limits_products
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no general_liability_policy_number', policy_number);

      return 'FAILED, no general_liability_policy_number';
    }

    const glPropertyMap = {
      general_liability_comm: 'comm',
      general_liability_claims_made: 'claims_made',
      general_liability_occur: 'occur',
      general_liability_gen_agg_policy: 'gen_agg_policy',
      general_liability_gen_agg_project: 'gen_agg_project',
      general_liability_gen_agg_loc: 'gen_agg_loc',
      other: 'gen_agg_other',
      general_liability_limits_each_occur: 'limits_each_occur',
      general_liability_limits_damage: 'limits_damage',
      general_liability_limits_med_exp: 'limits_med_exp',
      general_liability_limits_injury: 'limits_injury',
      general_liability_limits_gen_agg: 'limits_gen_agg',
      general_liability_limits_products: 'limits_products'
    };

    const properties = {
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_comm).id}`]: !!comm,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_claims_made).id}`]: !!claims_made,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_occur).id}`]: !!occur,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_policy).id}`]: !!gen_agg_policy,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_project).id}`]: !!gen_agg_project,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_gen_agg_loc).id}`]: !!gen_agg_loc,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.other).id}`]: !!gen_agg_other,
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_each_occur).id}`]: limits_each_occur ? `$${limits_each_occur}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_damage).id}`]: limits_damage ? `$${limits_damage}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_med_exp).id}`]: limits_med_exp ? `$${limits_med_exp}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_injury).id}`]: limits_injury ? `$${limits_injury}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_gen_agg).id}`]: limits_gen_agg ? `$${limits_gen_agg}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === glPropertyMap.general_liability_limits_products).id}`]: limits_products ? `$${limits_products}` : ''
    };

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapAutoProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      any_auto,
      owned_autos,
      hired_autos,
      non_owned_autos,
      scheduled_autos,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_combined_single,
      limits_injury_person,
      limits_injury_accident,
      limits_property_damage
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no auto_liability_policy_number', policy_number);

      return 'FAILED, no auto_liability_policy_number';
    }

    const autoPropertyMap = {
      auto_liability_any_auto: 'any_auto',
      auto_liability_owned_autos: 'owned_autos',
      auto_liability_scheduled_autos: 'scheduled_autos',
      auto_liability_hired_autos: 'hired_autos',
      auto_liability_non_owned_autos: 'non_owned_autos',
      auto_liability_limits_combined_single: 'limits_combined_single',
      auto_liability_limits_injury_person: 'limits_injury_person',
      auto_liability_limits_injury_accident: 'limits_injury_accident',
      auto_liability_limits_property_damage: 'limits_property_damage'
    };

    const properties = {
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_any_auto).id}`]: !!any_auto,
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_owned_autos).id}`]: !!owned_autos,
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_scheduled_autos).id}`]: !!scheduled_autos,
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_hired_autos).id}`]: !!hired_autos,
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_non_owned_autos).id}`]: !!non_owned_autos,
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_combined_single).id}`]: limits_combined_single ? `$${limits_combined_single}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_injury_person).id}`]: limits_injury_person ? `$${limits_injury_person}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_injury_accident).id}`]: limits_injury_accident ? `$${limits_injury_accident}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === autoPropertyMap.auto_liability_limits_property_damage).id}`]: limits_property_damage ? `$${limits_property_damage}` : ''
    };

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapUmbrellaExcessProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      umbrella,
      excess,
      umb_exc_occurrence,
      umb_exc_claims_made,
      umb_exc_limit_deductible,
      umb_exc_limit_retention,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_each_occur,
      limits_agg
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no umbrella_excess_policy_number', policy_number);

      return 'FAILED, no umbrella_excess_policy_number';
    }

    const uePropertyMap = {
      umbrella_excess_umbrella_liability: 'umbrella',
      umbrella_excess_excess_liability: 'excess',
      umbrella_excess_limits_each_occur: 'limits_each_occur',
      umbrella_excess_limits_agg: 'limits_agg'
    };

    const properties = {
      [`properties_${policyTypeProperties.find(prop => prop.name === uePropertyMap.umbrella_excess_umbrella_liability).id}`]: !!umbrella,
      [`properties_${policyTypeProperties.find(prop => prop.name === uePropertyMap.umbrella_excess_excess_liability).id}`]: !!excess,
      [`properties_${policyTypeProperties.find(prop => prop.name === uePropertyMap.umbrella_excess_limits_each_occur).id}`]: limits_each_occur ? `$${limits_each_occur}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === uePropertyMap.umbrella_excess_limits_agg).id}`]: limits_agg ? `$${limits_agg}` : ''
    };

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapWorkCompProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      any_excluded,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_disease_each_employee,
      limits_disease_policy,
      limits_each_accident
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no workers_comp_policy_number', policy_number);

      return 'FAILED, no workers_comp_policy_number';
    }

    const wcPropertyMap = {
      workers_comp_checked: 'any_excluded',
      workers_comp_limits_statutory_checked: 'per_statute',
      workers_comp_limits_other_checked: 'other',
      workers_comp_limits_each_accident: 'limits_each_accident',
      workers_comp_limits_disease_each_emp: 'limits_disease_each_employee',
      workers_comp_limits_disease_policy: 'limits_disease_policy'
    };

    const properties = {
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_checked).id}`]: !!any_excluded,
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_statutory_checked).id}`]: false,
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_other_checked).id}`]: false,
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_each_accident).id}`]: limits_each_accident ? `$${limits_each_accident}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_disease_each_emp).id}`]: limits_disease_each_employee ? `$${limits_disease_each_employee}` : '',
      [`properties_${policyTypeProperties.find(prop => prop.name === wcPropertyMap.workers_comp_limits_disease_policy).id}`]: limits_disease_policy ? `$${limits_disease_policy}` : ''
    };

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapCustomCoreDataToProperties = (typeName, customPolicyType, extractedData, corePolicyType) => {
    const { name: policyTypeName, properties } = customPolicyType;

    if (!policyTypeNameIsCoreFour(typeName)) {
      return false;
    }

    switch (typeName) {
      case 'general': {
        return mapCustomGeneralProperties(extractedData, properties);
      }

      case 'auto': {
        return mapCustomAutoProperties(extractedData, properties);
      }

      case 'umbrella_excess': {
        return mapCustomUmbrellaExcessProperties(extractedData, properties, corePolicyType);
      }

      case 'workers_comp': {
        return mapCustomWorkCompProperties(extractedData, properties, corePolicyType);
      }

      default:
        throw new Error(`Something went wrong extracting the policy details for ${policyTypeName}`);
    }
  };

  const mapCustomGeneralProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      comm,
      claims_made,
      occur,
      gen_agg_project,
      gen_agg_policy,
      gen_agg_loc,
      gen_agg_other,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_each_occur,
      limits_damage,
      limits_med_exp,
      limits_injury,
      limits_gen_agg,
      limits_products
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no general_liability_policy_number', policy_number);

      return 'FAILED, no general_liability_policy_number';
    }

    const propertySearcher = new FuzzySearch(policyTypeProperties, ['displayName', 'name'], { sort: true });
    const properties = {};

    const commProperty = fuzzyMatchSearch(propertySearcher, ['Commercial General Liability', 'Commercial General'], DataTypes.Boolean);
    const claimsProperty = fuzzyMatchSearch(propertySearcher, ['Per Claims Made', 'Per Claims', 'Claims-Made', 'Claims'], DataTypes.Boolean);
    const occurProperty = fuzzyMatchSearch(propertySearcher, ['Per Occurrence', 'Per Ocurrence', 'Per Occurence'], DataTypes.Boolean);
    const genAggPolicyProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Policy', 'Per Policy'], DataTypes.Boolean);
    const genAggProjectProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Project', 'Per Project'], DataTypes.Boolean);
    const genAggLocProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Location', 'Per Location', 'Per Loc'], DataTypes.Boolean);
    const otherProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate Limit Applies Per Other', 'Per Other', 'Other'], DataTypes.Boolean);
    const eachOccurProperty = fuzzyMatchSearch(propertySearcher, ['Each Occurrence', 'Each Ocurrence', 'Each Occurence', 'Ea Occur', 'Each Occur', DataTypes.Monetary]);
    const damageProperty = fuzzyMatchSearch(propertySearcher, ['Damage to Rented Premises', 'Rented Premises', 'Damage to', 'Damage', DataTypes.Monetary]);
    const medExpProperty = fuzzyMatchSearch(propertySearcher, ['Medical Expenses', 'Medical Exp', 'Med Exp', DataTypes.Monetary]);
    const injuryProperty = fuzzyMatchSearch(propertySearcher, ['Personal and Adv Injury', 'Personal Injury', 'Personal', 'Personal &', 'Injury', DataTypes.Monetary]);
    const genAggLimitProperty = fuzzyMatchSearch(propertySearcher, ['General Aggregate', 'General Agregate', 'Gen Agg', DataTypes.Monetary]);
    const productProperty = fuzzyMatchSearch(propertySearcher, ['Products - Comp/OP Agg', 'Products -', 'Products', DataTypes.Monetary]);

    if (commProperty) {
      properties[`properties_${commProperty.id}`] = !!comm;
    }

    if (claimsProperty) {
      properties[`properties_${claimsProperty.id}`] = !!claims_made;
    }

    if (occurProperty) {
      properties[`properties_${occurProperty.id}`] = !!occur;
    }

    if (genAggPolicyProperty) {
      properties[`properties_${genAggPolicyProperty.id}`] = !!gen_agg_policy;
    }

    if (genAggProjectProperty) {
      properties[`properties_${genAggProjectProperty.id}`] = !!gen_agg_project;
    }

    if (genAggLocProperty) {
      properties[`properties_${genAggLocProperty.id}`] = !!gen_agg_loc;
    }

    if (otherProperty) {
      properties[`properties_${otherProperty.id}`] = !!gen_agg_other;
    }

    if (eachOccurProperty) {
      properties[`properties_${eachOccurProperty.id}`] = limits_each_occur ? `$${limits_each_occur}` : '';
    }

    if (damageProperty) {
      properties[`properties_${damageProperty.id}`] = limits_damage ? `$${limits_damage}` : '';
    }

    if (medExpProperty) {
      properties[`properties_${medExpProperty.id}`] = limits_med_exp ? `$${limits_med_exp}` : '';
    }

    if (injuryProperty) {
      properties[`properties_${injuryProperty.id}`] = limits_injury ? `$${limits_injury}` : '';
    }

    if (genAggLimitProperty) {
      properties[`properties_${genAggLimitProperty.id}`] = limits_gen_agg ? `$${limits_gen_agg}` : '';
    }

    if (productProperty) {
      properties[`properties_${productProperty.id}`] = limits_products ? `$${limits_products}` : '';
    }

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapCustomAutoProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      any_auto,
      owned_autos,
      hired_autos,
      non_owned_autos,
      scheduled_autos,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_combined_single,
      limits_injury_person,
      limits_injury_accident,
      limits_property_damage
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no auto_liability_policy_number', policy_number);

      return 'FAILED, no auto_liability_policy_number';
    }

    const propertySearcher = new FuzzySearch(policyTypeProperties, ['displayName', 'name'], { sort: true });
    const properties = {};

    const anyAutoProperty = fuzzyMatchSearch(propertySearcher, ['Any Auto'], DataTypes.Boolean);
    const ownedAutoProperty = fuzzyMatchSearch(propertySearcher, ['Owned Auto', 'Owned Auto'], DataTypes.Boolean);
    const scheduledAutoProperty = fuzzyMatchSearch(propertySearcher, ['Scheduled Autos', 'Scheduled Auto', 'Scheduled'], DataTypes.Boolean);
    const hiredAutoProperty = fuzzyMatchSearch(propertySearcher, ['Hired Autos Only', 'Hired Auto'], DataTypes.Boolean);
    const nonOwnedAutoProperty = fuzzyMatchSearch(propertySearcher, ['Non-Owned Autos Only', 'Non-Owned Auto', 'Non-Owned', 'Non Owned'], DataTypes.Boolean);
    const combinedSingleProperty = fuzzyMatchSearch(propertySearcher, ['Combined Single Limit Each Accident', 'Combined Single Limit Ea Accident', 'Combined Single Limit'], DataTypes.Monetary);
    const injuryLimitProperty = fuzzyMatchSearch(propertySearcher, ['Bodily Injury Per Person', 'Injury Person'], DataTypes.Monetary);
    const injuryAccidentLimitProperty = fuzzyMatchSearch(propertySearcher, ['Bodily Injury Per Accident', 'Injury Accident'], DataTypes.Monetary);
    const propertyLimitProperty = fuzzyMatchSearch(propertySearcher, ['Property Damage Per Accident', 'Property Damage'], DataTypes.Monetary);

    if (anyAutoProperty) {
      properties[`properties_${anyAutoProperty.id}`] = !!any_auto;
    }

    if (ownedAutoProperty) {
      properties[`properties_${ownedAutoProperty.id}`] = !!owned_autos;
    }

    if (scheduledAutoProperty) {
      properties[`properties_${scheduledAutoProperty.id}`] = !!scheduled_autos;
    }

    if (hiredAutoProperty) {
      properties[`properties_${hiredAutoProperty.id}`] = !!hired_autos;
    }

    if (nonOwnedAutoProperty) {
      properties[`properties_${nonOwnedAutoProperty.id}`] = !!non_owned_autos;
    }

    if (combinedSingleProperty) {
      properties[`properties_${combinedSingleProperty.id}`] = limits_combined_single ? `$${limits_combined_single}` : '';
    }

    if (injuryLimitProperty) {
      properties[`properties_${injuryLimitProperty.id}`] = limits_injury_person ? `$${limits_injury_person}` : '';
    }

    if (injuryAccidentLimitProperty) {
      properties[`properties_${injuryAccidentLimitProperty.id}`] = limits_injury_accident ? `$${limits_injury_accident}` : '';
    }

    if (propertyLimitProperty) {
      properties[`properties_${propertyLimitProperty.id}`] = limits_property_damage ? `$${limits_property_damage}` : '';
    }

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapCustomUmbrellaExcessProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      umbrella,
      excess,
      umb_exc_occurrence,
      umb_exc_claims_made,
      umb_exc_limit_deductible,
      umb_exc_limit_retention,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_each_occur,
      limits_agg
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no umbrella_excess_policy_number', policy_number);

      return 'FAILED, no umbrella_excess_policy_number';
    }

    const propertySearcher = new FuzzySearch(policyTypeProperties, ['displayName', 'name'], { sort: true });
    const properties = {};

    const umbrellaProperty = fuzzyMatchSearch(propertySearcher, ['Umbrella Liability', 'Umbrella Liab', 'Umbrella'], DataTypes.Boolean);
    const excessProperty = fuzzyMatchSearch(propertySearcher, ['Excess Liability', 'Excess Liab', 'Excess'], DataTypes.Boolean);
    const occurProperty = fuzzyMatchSearch(propertySearcher, ['Occur'], DataTypes.Boolean);
    const claimsMadeProperty = fuzzyMatchSearch(propertySearcher, ['Claims-Made', 'Claims Made'], DataTypes.Monetary);
    const eachOccurProperty = fuzzyMatchSearch(propertySearcher, ['Each Occurrence', 'Each Occurence', 'Each Ocurrence', 'Occurrence', 'Ea Occur'], DataTypes.Monetary);
    const aggProperty = fuzzyMatchSearch(propertySearcher, ['Aggregate', 'Agregate'], DataTypes.Monetary);

    if (umbrellaProperty) {
      properties[`properties_${umbrellaProperty.id}`] = !!umbrella;
    }

    if (excessProperty) {
      properties[`properties_${excessProperty.id}`] = !!excess;
    }

    if (occurProperty) {
      properties[`properties_${occurProperty.id}`] = !!umb_exc_occurrence;
    }

    if (claimsMadeProperty) {
      properties[`properties_${claimsMadeProperty.id}`] = !!umb_exc_claims_made;
    }

    if (eachOccurProperty) {
      properties[`properties_${eachOccurProperty.id}`] = limits_each_occur ? `$${limits_each_occur}` : '';
    }

    if (aggProperty) {
      properties[`properties_${aggProperty.id}`] = limits_agg ? `$${limits_agg}` : '';
    }

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const mapCustomWorkCompProperties = (extractedData, policyTypeProperties) => {
    const {
      insurance_company,
      any_excluded,
      addl_insured,
      subr_waived,
      policy_number,
      policy_effective_date,
      policy_expired_date,
      limits_disease_each_employee,
      limits_disease_policy,
      limits_each_accident
    } = extractedData;

    if (!policy_number) {
      console.log('FAILED, no workers_comp_policy_number', policy_number);

      return 'FAILED, no workers_comp_policy_number';
    }

    const propertySearcher = new FuzzySearch(policyTypeProperties, ['displayName', 'name'], { sort: true });
    const properties = {};

    const anyExcludedProperty = fuzzyMatchSearch(propertySearcher, ['Any Excluded', 'Excluded'], DataTypes.Boolean);
    const perStatuteProperty = fuzzyMatchSearch(propertySearcher, ['Per Statute', 'Statute'], DataTypes.Boolean);
    const otherProperty = fuzzyMatchSearch(propertySearcher, ['Other'], DataTypes.Boolean);
    const eachAccidentProperty = fuzzyMatchSearch(propertySearcher, ['E.L. Each Accident', 'E.L. Ea Accident', 'EL Each', 'Each Accident'], DataTypes.Monetary);
    const diseaseEachEmployeeProperty = fuzzyMatchSearch(propertySearcher, ['E.L. Disease Each Employee', 'Disease Each Employee', 'Ea Employee', 'Employee'], DataTypes.Monetary);
    const diseasePolicyProperty = fuzzyMatchSearch(propertySearcher, ['E.L. Disease Policy Limit', 'Disease Policy Limit', 'Policy Limit', 'Policy'], DataTypes.Monetary);

    if (anyExcludedProperty) {
      properties[`properties_${anyExcludedProperty.id}`] = !!any_excluded;
    }

    if (perStatuteProperty) {
      properties[`properties_${perStatuteProperty.id}`] = false;
    }

    if (otherProperty) {
      properties[`properties_${otherProperty.id}`] = false;
    }

    if (eachAccidentProperty) {
      properties[`properties_${eachAccidentProperty.id}`] = limits_each_accident ? `$${limits_each_accident}` : '';
    }

    if (diseaseEachEmployeeProperty) {
      properties[`properties_${diseaseEachEmployeeProperty.id}`] = limits_disease_each_employee ? `$${limits_disease_each_employee}` : '';
    }

    if (diseasePolicyProperty) {
      properties[`properties_${diseasePolicyProperty.id}`] = limits_disease_policy ? `$${limits_disease_policy}` : '';
    }

    return {
      details: {
        additional_insured: !!addl_insured,
        effective_at: isValidDateString(policy_effective_date)
          ? new Date(policy_effective_date)
          : null,
        expires_at: isValidDateString(policy_expired_date)
          ? new Date(policy_expired_date)
          : null,
        number: policy_number,
        subrogation_waived: !!subr_waived
      },
      insurance_company,
      ...properties
    };
  };

  const fuzzyMatchSearch = (fuzzyMatchSearcher, searchWords = [], dataType = null) => {
    for (let i = 0; i < searchWords.length; i++) {
      const searchWord = searchWords[i].toLowerCase();
      const propertySearch = fuzzyMatchSearcher.search(searchWord);

      if (propertySearch.length) {
        if (dataType) {
          const property = propertySearch.find(p => p.dataType === dataType);

          if (property) {
            return property;
          }
        } else {
          return propertySearch[0];
        }
      }
    }

    return null;
  };

  const setOtherPolicyTypeData = otherData => (dispatch, getState) => {
    const companyPolicyTypes = selectCompanyPolicyTypes(getState());
    const policyRequirements = selectRequirementPolicyTypes(getState());
    const selectedRequirements = selectSelectedPolicyTypes(getState());
    const currentPolicyData = selectPolicyData(getState());
    const { chooseAnyPolicyType } = selectOptions(getState());

    const otherTypeArray = Object.entries(otherData);
    const allPolicyReqs = [...selectedRequirements, ...policyRequirements];
    const otherTypeNameMap = otherTypeArray.map(([typeName, details]) => ({
      name: typeName,
      displayName: typeName.replace(/_/g, ' '),
      details
    }));

    // go through all the other requirements
    allPolicyReqs.forEach(req => {
      // ignore core four policy types
      if ([PolicyTypes.General, PolicyTypes.Auto, PolicyTypes.UmbrellaExcess, PolicyTypes.WorkersComp].includes(req.name)) {
        return;
      }

      if (req.id in currentPolicyData) {
        console.log(`${req.name} is already set.  Not setting ${req.name} data`);

        return;
      }

      const searchName = req.name.replace(/[_\-&–]/g, ' ');

      const policyTypeSearcher = new FuzzySearch(otherTypeNameMap, ['displayName', 'name'], { sort: true });
      const fuzzySearchOnCustomPolicyTypes = policyTypeSearcher.search(searchName);

      let extractedPolicyData = null;

      if (!fuzzySearchOnCustomPolicyTypes.length) {
        console.log(`Cannot find fuzzy match for policy type, ${req.displayName}.  Trying to fuzzy match in the reverse direction`);

        otherTypeNameMap.forEach(otherType => {
          const reverseFuzzySearcher = new FuzzySearch(allPolicyReqs, ['displayName', 'name'], { sort: true });
          const reverseSearch = reverseFuzzySearcher.search(otherType.displayName);

          if (reverseSearch.length && reverseSearch[0].id === req.id) {
            console.log(`Found match in reverse direction for ${req.displayName}.  Using other policy data: ${otherType.name}`);

            extractedPolicyData = otherType;
          }
        });

        if (!extractedPolicyData) {
          console.log(
            `Cannot find fuzzy match for policy type, ${req.displayName}.  Not setting ${req.displayName} data`,
            {
              fuzzySearchOnCustomPolicyTypes,
              otherTypeNameMap,
              req
            }
          );

          return;
        }
      }

      if (!extractedPolicyData) {
        extractedPolicyData = fuzzySearchOnCustomPolicyTypes[0];
      }

      const {
        insurance_company,
        policy_number,
        policy_effective_date,
        policy_expired_date,
        limits,
        suggested_limits
      } = extractedPolicyData.details;

      const { properties: policyReqProperties } = req;
      const limitsArray = Object.entries(limits).map(([limitName, limitValue]) => ({
        limitName: limitName.replace(/_/g, ' '),
        limitValue
      }));

      const properties = {};
      const suggestedLimits = {};

      // go through each known property and see if any of our limits match it
      policyReqProperties.forEach((reqProperty, i) => {
        const propertySearcher = new FuzzySearch(limitsArray, ['limitName'], { sort: true });
        const fuzzySearchOnProperty = propertySearcher.search(reqProperty.displayName);

        const customLimitProperty = fuzzySearchOnProperty[0];

        if (!customLimitProperty) {
          console.log(`Can't find an extracted policy property for ${reqProperty.displayName}`);

          if (suggested_limits.length) {
            console.log(`Setting suggested limits for ${reqProperty.displayName}`);

            if (reqProperty.dataType === DataTypes.Monetary) {
              suggestedLimits[`properties_${reqProperty.id}`] = suggested_limits;
            }
          }

          return;
        }

        if (reqProperty.dataType === DataTypes.Monetary) {
          properties[`properties_${reqProperty.id}`] = customLimitProperty.limitValue ? `$${customLimitProperty.limitValue}` : '';
        } else if (reqProperty.dataType === DataTypes.Boolean) {
          properties[`properties_${reqProperty.id}`] = !!customLimitProperty.limitValue;
        } else if (reqProperty.dataType === DataTypes.String) {
          properties[`properties_${reqProperty.id}`] = customLimitProperty.limitValue ?? '';
        }
      });

      const customPolicyData = {
        details: {
          additional_insured: null,
          effective_at: isValidDateString(policy_effective_date)
            ? new Date(policy_effective_date)
            : null,
          expires_at: isValidDateString(policy_expired_date)
            ? new Date(policy_expired_date)
            : null,
          number: policy_number,
          subrogation_waived: null
        },
        insurance_company,
        suggestedLimits,
        ...properties
      };

      const enhancedCustomData = getEnhancedPolicyData(
        req,
        camelcaseKeys(insurance_company, { deep: true }),
        customPolicyData
      );

      dispatch(setPolicyData(req.id, enhancedCustomData));

      console.log(`Custom Policy Data for ${req.name} should have been set using other extracted policy: ${extractedPolicyData.name}`);
    });

    // otherTypeArray.forEach(([typeName, details]) => {
    //   const policyTypeNameFromSnakeCase = typeName.replace(/_/g, ' ');
    //   const policyTypeSearcher = new FuzzySearch(companyPolicyTypes, ['displayName', 'name'], { sort: true });
    //   const fuzzySearchOnCustomPolicyTypes = policyTypeSearcher.search(policyTypeNameFromSnakeCase);
    //
    //   if (!fuzzySearchOnCustomPolicyTypes.length) {
    //     console.log(
    //       `Cannot find fuzzy match for policy type, ${policyTypeNameFromSnakeCase}.  Not setting ${policyTypeNameFromSnakeCase} data`
    //     );
    //
    //     return false;
    //   }
    //
    //   const customPolicyType = fuzzySearchOnCustomPolicyTypes[0];
    //
    //   if (customPolicyType.id in currentPolicyData) {
    //     console.log(`${typeName} is already set.  Not setting ${typeName} data`);
    //
    //     return false;
    //   }
    //
    //   const { properties: customProperties } = customPolicyType;
    //   const policyRequirementsCustom = policyRequirements.find(policyType => policyType.name === customPolicyType.name);
    //   const selectedRequirementsCustom = selectedRequirements.find(policyType => policyType.name === customPolicyType.name);
    //
    //   const policyRequirementsHasCustom = policyRequirementsCustom ? !!Object.keys(policyRequirementsCustom).length : false;
    //   const selectedPolicyRequirementsHasCustom = selectedRequirementsCustom ? !!Object.keys(selectedRequirementsCustom).length : false;
    //
    //   if (!policyRequirementsHasCustom && !selectedPolicyRequirementsHasCustom) {
    //     console.log(
    //       'FAILED, no policyRequirementsHasCustom and no selectedPolicyRequirementsHasCustom',
    //       policyRequirementsHasCustom,
    //       selectedPolicyRequirementsHasCustom
    //     );
    //
    //     return false;
    //   }
    //
    //   if (chooseAnyPolicyType && !selectedPolicyRequirementsHasCustom) {
    //     console.log(`The custom policy type, ${customPolicyType.name}, has not been selected for submission, skipping`);
    //
    //     return false;
    //   }
    //
    //   const {
    //     insurance_company,
    //     policy_number,
    //     policy_effective_date,
    //     policy_expired_date,
    //     limits
    //   } = details;
    //
    //   const properties = {};
    //
    //   Object.keys(limits).forEach((limitName, i) => {
    //     const searcher = new FuzzySearch(customProperties, ['displayName', 'name'], { sort: true });
    //     const fuzzySearchOnCustomProperties = searcher.search(limitName);
    //
    //     // if we can't fuzzy match on the custom property name, do one more check
    //     if (!fuzzySearchOnCustomProperties.length) {
    //       console.log(
    //         `Cannot fuzzy match for ${limitName} on ${typeName}.  Checking for unknown limit value...`
    //       );
    //
    //       if (limitName === `limit_${i}`) {
    //         // ML couldn't figure out the limit type, so lets just set the values for each custom property
    //         for (let j = 0; j < customProperties.length; j++) {
    //           const customProperty = customProperties[j];
    //
    //           // if the custom property hasn't been set and it's a monetary limit, set the limit
    //           if (!properties[`properties_${customProperty.id}`] && customProperty.dataType === DataTypes.Monetary) {
    //             console.log(
    //               `Setting ${limits[limitName]} on ${customProperty.name} for ${typeName}.`
    //             );
    //
    //             properties[`properties_${customProperty.id}`] = limits[limitName] ? `$${limits[limitName]}` : '';
    //
    //             break;
    //           }
    //         }
    //       } else {
    //         console.log(
    //           `${typeName} does not have a matching property for ${limitName}.  Not setting ${typeName} data`
    //         );
    //
    //         return false;
    //       }
    //     } else {
    //       const customLimitProperty = fuzzySearchOnCustomProperties[0];
    //
    //       if (!customLimitProperty) {
    //         console.log(`${typeName} does not have a matching property for ${limitName}.  Not setting ${typeName} data`);
    //
    //         return false;
    //       }
    //
    //       if (customLimitProperty.dataType === DataTypes.Monetary) {
    //         properties[`properties_${customLimitProperty.id}`] = limits[limitName] ? `$${limits[limitName]}` : '';
    //       } else if (customLimitProperty.dataType === DataTypes.Boolean) {
    //         properties[`properties_${customLimitProperty.id}`] = !!limits[limitName];
    //       } else if (customLimitProperty.dataType === DataTypes.String) {
    //         properties[`properties_${customLimitProperty.id}`] = limits[limitName] ?? '';
    //       }
    //     }
    //   });
    //
    //   const customPolicyData = {
    //     details: {
    //       additional_insured: null,
    //       effective_at: new Date(policy_effective_date),
    //       expires_at: new Date(policy_expired_date),
    //       number: policy_number,
    //       subrogation_waived: null
    //     },
    //     insurance_company,
    //     ...properties
    //   };
    //
    //   const enhancedCustomData = getEnhancedPolicyData(
    //     customPolicyType,
    //     camelcaseKeys(insurance_company, { deep: true }),
    //     customPolicyData
    //   );
    //
    //   dispatch(setPolicyData(customPolicyType.id, enhancedCustomData));
    //
    //   console.log(`Custom Policy Data for ${typeName} should have been set`);
    //
    //   return enhancedCustomData;
    // });
  };

  const policyTypeNameIsCoreFour = policyTypeName => {
    if (!['general', 'auto', 'umbrella_excess', 'workers_comp'].includes(policyTypeName)) {
      throw new Error(`${policyTypeName} is not a core four acord form policy type`);
    }

    return true;
  };

  const setOcrIsCanceled = bool => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_OCR_IS_CANCELED,
      bool
    });
  };

  const removeDocument = index => dispatch => {
    dispatch({
      name,
      type: CERTS_REMOVE_DOCUMENT,
      index
    });
  };

  const setCurrentFile = index => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_CURRENT_FILE,
      index
    });
  };

  const nextStep = (count = 1) => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_CURRENT_STEP,
      add: count
    });
  };

  const setFirstStep = (stepNumber = 1) => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_FIRST_STEP,
      stepNumber
    });
  };

  const goBack = (count = 1) => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_CURRENT_STEP,
      add: -(Math.abs(count))
    });
  };

  const setAgencyId = agencyId => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_AGENCY_ID,
      agencyId
    });
  };

  const setCompany = company => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_COMPANY,
      company
    });
  };

  const setJob = job => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_JOB,
      job
    });
  };

  const setDepartment = department => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_DEPARTMENT,
      department
    });
  };

  const setDocumentRequirements = documentRequirements => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_DOCUMENT_REQUIREMENTS,
      documentRequirements
    });
  };

  const setLanguageRequirements = languageRequirements => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_LANGUAGE_REQUIREMENTS,
      languageRequirements
    });
  };

  const setOptions = options => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_OPTIONS,
      options
    });
  };

  const setItemRequirements = itemRequirements => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_ITEM_REQUIREMENTS,
      itemRequirements
    });
  };

  const setUser = user => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_USER,
      user
    });
  };

  const setPolicyReviewContentHeight = height => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_POLICY_REVIEW_CONTENT_HEIGHT,
      height
    });
  };

  const setInsuredRequirements = insuredRequirements => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_INSURED_REQUIREMENTS,
      insuredRequirements
    });
  };

  const setInsured = insured => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_INSURED,
      insured
    });
  };

  const setInsuranceRequirement = insuranceRequirement => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_INSURANCE_REQUIREMENT,
      insuranceRequirement
    });
  };

  const setInsurancePolicyTypes = insurancePolicyTypes => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_INSURANCE_POLICY_TYPES,
      companyPolicyTypes: insurancePolicyTypes
    });
  };

  const setRequiredPolicyTypes = () => (dispatch, getState) => {
    const minimumRequirements = selectMinimumRequirements(getState());
    const policyTypes = selectCompanyPolicyTypes(getState());
    const additionalPolicyTypes = policyTypes.filter(type => !minimumRequirements[type.id] && AdditionalPolicyTypeNames.includes(type.name));
    const requirementPolicyTypes = policyTypes.filter(type => !!minimumRequirements[type.id]);

    dispatch({
      name,
      type: CERTS_SET_REQUIRED_POLICY_TYPES,
      requirementPolicyTypes,
      additionalPolicyTypes
    });
  };

  const addAdditionalInsurancePolicyType = policyType => dispatch => {
    dispatch({
      name,
      type: CERTS_ADD_ADDITIONAL_INSURANCE_POLICY_TYPE,
      policyType
    });
  };

  const removeAdditionalInsurancePolicyType = policyType => dispatch => {
    dispatch({
      name,
      type: CERTS_REMOVE_ADDITIONAL_INSURANCE_POLICY_TYPE,
      policyType
    });
  };

  const addSelectedInsurancePolicyType = policyType => dispatch => {
    dispatch({
      name,
      type: CERTS_ADD_SELECTED_INSURANCE_POLICY_TYPE,
      policyType
    });
  };

  const removeSelectedInsurancePolicyType = policyTypeId => dispatch => {
    dispatch(skipPolicyType(policyTypeId));

    dispatch({
      name,
      type: CERTS_REMOVE_SELECTED_INSURANCE_POLICY_TYPE,
      policyTypeId
    });
  };

  const setPolicyData = (policyTypeId, data) => dispatch => {
    dispatch({
      name,
      type: CERTS_SET_POLICY_DATA,
      policyTypeId,
      data,
      camelCase: false
    });
  };

  const nextPolicyType = () => dispatch => {
    dispatch({
      name,
      type: CERTS_NEXT_POLICY_TYPE
    });
  };

  const skipPolicyType = policyTypeId => dispatch => {
    dispatch({
      name,
      type: CERTS_SKIP_POLICY_TYPE,
      policyTypeId
    });
  };

  const previousPolicyType = () => dispatch => {
    dispatch({
      name,
      type: CERTS_PREVIOUS_POLICY_TYPE
    });
  };

  const submit = (onSuccess = () => {}, onFail = () => {}) => async (dispatch, getState) => {
    dispatch({
      name,
      type: CERTS_SUBMIT
    });

    const options = selectOptions(getState());
    const { insuredId } = selectInsured(getState());
    const policyData = selectPolicyData(getState());
    const documents = selectAllDocuments(getState());
    const { id: recordId } = selectInsuranceRequirement(getState());
    const selectedAdditionalPolicyTypes = selectSelectedAdditionalPolicyTypes(getState());
    const agencyId = selectAgencyId(getState());
    const gptRequestId = selectGptRequestId(getState());
    const { aiScan: aiFeatureEnabled } = options;

    dispatch(createToast({
      title: 'Submission Processing',
      duration: 60000
    }));

    try {
      const policySubmissionArray = Object
        .entries(policyData)
        .reduce((array, [policyTypeId, {
          details = {},
          properties = [],
          skipped
        }]) => {
          if (!skipped) {
            return [
              ...array,
              {
                requirement_id: recordId,
                policy_type_id: policyTypeId,
                details,
                properties
              }
            ];
          }

          return array;
        }, []);

      const docSubmissionArray = documents.map(doc => {
        const { preparedDocuments, linkedPolicyTypeIds } = doc;

        return {
          documents: preparedDocuments,
          linked_policy_type_ids: linkedPolicyTypeIds
        };
      });

      const additionalPolicyIdMap = createMap(selectedAdditionalPolicyTypes, 'id');

      const submission = portalApi.submitRecordCertificate(
        recordId,
        {
          policy_data: policySubmissionArray,
          document_data: docSubmissionArray,
          additional_policy_type_id_map: additionalPolicyIdMap ?? [],
          send_submission_email: options.sendSubmissionEmail,
          should_sync: options.shouldSync,
          agency_id: agencyId
        }
      );

      let publishGptRequestAnswersPromise;
      if (aiFeatureEnabled && gptRequestId) {
        publishGptRequestAnswersPromise = portalApi.publishGptRequestAnswers({ recordId, gptRequestId, sync: options.shouldSync });

        await submission;
      } else {
        await submission;
      }

      if (options.updateInsuredProfilePage) {
        dispatch({
          name,
          type: CERTS_SUBMIT_SUCCESS
        });

        dispatch(hideTray());

        dispatch(createToast({
          title: 'Submission Uploaded!',
          description: 'Updating insured profile page...',
          duration: 60000
        }));

        await Promise.all([
          queryClient.invalidateQueries(recordKeys.recordPolicyRequirements(recordId)),
          queryClient.invalidateQueries(recordKeys.recordPolicies(recordId)),
          queryClient.invalidateQueries(recordKeys.recordDocumentRequirements(recordId)),
          queryClient.invalidateQueries(recordKeys.recordDocuments(recordId)),
          queryClient.invalidateQueries(recordKeys.recordItemRequirements(recordId)),
          queryClient.invalidateQueries(recordKeys.recordLanguageRequirements(recordId)),
          queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId))
        ]);

        if (aiFeatureEnabled && gptRequestId) {
          dispatch(createToast({
            title: 'PINS AI Assistant finishing scan...',
            duration: 60000
          }));

          await publishGptRequestAnswersPromise;

          await Promise.all([
            queryClient.invalidateQueries(recordKeys.recordQuestionRequirements(recordId)),
            queryClient.invalidateQueries(recordKeys.recordGptRequests(recordId))
          ]);
        }

        dispatch(createSuccessToast({ title: 'Page updated!' }));
      } else {
        dispatch({
          name,
          type: CERTS_SUBMIT_SUCCESS
        });

        dispatch(createSuccessToast({ title: 'Submission Uploaded' }));
      }

      onSuccess();

      dispatch(reset());
    } catch (error) {
      dispatch({
        name,
        type: CERTS_SUBMIT_FAILURE,
        error
      });

      // invalidate any keys on the record
      Promise.all([
        queryClient.invalidateQueries(recordKeys.record(recordId))
      ]);

      if (error.status === 400 && error.message === 'No policy or document data provided') {
        dispatch(createToast({
          title: 'Oops.  You must submit at least one policy or document',
          status: 'warning'
        }));

        onFail();
        return;
      }

      dispatch(createErrorToast({ title: 'Oops, something went wrong with your submission', error }));
      onFail();
    }
  };

  const listInsuranceCompanies = debounceAction(
    ({
      page = 1,
      pageSize = 100,
      searchTerm = ''
    } = {}) => async (dispatch, getState) => {
      const { companyId } = selectCompany(getState());

      dispatch({
        name,
        type: CERTS_LIST_INSURANCE_COMPANIES,
        companyId,
        pageData: {
          page,
          pageSize
        },
        searchTerm
      });

      try {
        const {
          data,
          ...pageInfo
        } = await portalApi.listCompanyInsuranceCompanies(
          companyId,
          {
            page,
            pageSize
          },
          { company_name: searchTerm }
        );

        dispatch({
          name,
          type: CERTS_LIST_INSURANCE_COMPANIES_SUCCESS,
          data,
          pageInfo
        });
      } catch (error) {
        dispatch({
          name,
          type: CERTS_LIST_INSURANCE_COMPANIES_FAILURE,
          error
        });
      }
    }
  );

  const getDocumentTypes = debounceAction(
    (filter = {}) => async (dispatch, getState) => {
      const { companyId } = selectCompany(getState());
      const documentRequirements = selectDocumentRequirements(getState());

      dispatch({
        name,
        type: CERTS_GET_DOCUMENT_TYPES,
        companyId,
        filter
      });

      try {
        const data = await portalApi.getCompanyDocumentTypes(companyId, filter);
        const requiredDocTypeIds = documentRequirements.map(docReq => docReq.insuranceDocumentTypeId);
        const filterRequiredDocs = data.filter(docType => !requiredDocTypeIds.includes(docType.id));

        dispatch({
          name,
          type: CERTS_GET_DOCUMENT_TYPES_SUCCESS,
          data: filterRequiredDocs
        });
      } catch (error) {
        dispatch({
          name,
          type: CERTS_GET_DOCUMENT_TYPES_FAILURE,
          error
        });
      }
    }
  );

  const getUserPolicies = () => async (dispatch, getState) => {
    const { insuredId } = selectInsured(getState());
    const { jobId } = selectJob(getState());
    const { userId } = selectUser(getState());
    const requirements = selectInsuredRequirements(getState());
    const policyTypes = selectCompanyPolicyTypes(getState());
    const { id: requirementId } = selectInsuranceRequirement(getState());

    dispatch({
      name,
      type: CERTS_GET_USER_POLICIES,
      insuredId,
      jobId,
      userId
    });

    try {
      const response = await portalApi.getRecordPolicies(requirementId);
      const userPolicies = response.filter(policy => policy.owner_id === userId);

      const userPoliciesWithRequirementStatuses = v2MatchPoliciesToRequirements(
        policyTypes,
        userPolicies,
        requirements,
        false
      );

      const userPoliciesWithLoaders = userPoliciesWithRequirementStatuses.map(policy => ({
        ...policy,
        isLoading: false
      }));

      dispatch({
        name,
        type: CERTS_GET_USER_POLICIES_SUCCESS,
        userPoliciesWithLoaders
      });
    } catch (error) {
      dispatch({
        name,
        type: CERTS_GET_USER_POLICIES_FAILURE,
        error
      });
    }
  };

  const getUserDocuments = () => async (dispatch, getState) => {
    const { insuredId } = selectInsured(getState());
    const { jobId } = selectJob(getState());
    const { userId } = selectUser(getState());
    const { id: recordId } = selectInsuranceRequirement(getState());

    dispatch({
      name,
      type: CERTS_GET_USER_DOCUMENTS,
      insuredId,
      jobId,
      userId
    });

    try {
      const response = await portalApi.getRecordDocuments(recordId);
      const userDocuments = response.filter(doc => doc.user_id === userId && doc.status !== Statuses.Created);

      dispatch({
        name,
        type: CERTS_GET_USER_DOCUMENTS_SUCCESS,
        userDocuments
      });
    } catch (error) {
      dispatch({
        name,
        type: CERTS_GET_USER_DOCUMENTS_FAILURE
      });
    }
  };

  const deleteDocument = (docId, fetchDocs = false, onSuccess = () => {}) => async dispatch => {
    dispatch({
      name,
      type: CERTS_DELETE_DOCUMENT,
      docId
    });

    dispatch(createToast({ title: 'Deleting Document' }));

    try {
      const response = await portalApi.updateDocument(docId, { status: Statuses.Deleted });
      dispatch({
        name,
        type: CERTS_DELETE_DOCUMENT_SUCCESS,
        response
      });

      dispatch(createSuccessToast({ title: 'Document Successfully Deleted.' }));
      onSuccess();

      if (fetchDocs) {
        dispatch(getUserDocuments());
      }
    } catch (error) {
      dispatch({
        name,
        type: CERTS_DELETE_DOCUMENT_FAILURE,
        error
      });

      dispatch(createErrorToast({ title: 'Oops, looks like something went wrong', error }));
    }
  };

  const updatePolicy = (policyId, policyData) => async dispatch => {
    policyData.notify_cert_holder = true;

    dispatch({
      name,
      type: CERTS_UPDATE_POLICY,
      policyId,
      policyData
    });

    dispatch(createToast({ title: 'Updating policy' }));

    try {
      const response = await portalApi.updateInsurancePolicy(policyId, policyData);
      dispatch({
        name,
        type: CERTS_UPDATE_POLICY_SUCCESS,
        response
      });

      dispatch(createSuccessToast({ title: 'Policy updated successfully!' }));
    } catch (error) {
      dispatch({
        name,
        type: CERTS_UPDATE_POLICY_FAILURE,
        error,
        policyId
      });

      dispatch(createErrorToast({ title: 'Policy failed to update.', error }));
    }
  };

  const createInsuranceCompany = companyName => async (dispatch, getState) => {
    dispatch({
      name,
      type: CERTS_CREATE_INSURANCE_COMPANY,
      companyName
    });

    const { companyId } = selectCompany(getState());

    try {
      const response = await portalApi.createInsuranceCompany(companyId, companyName);
      dispatch({
        name,
        type: CERTS_CREATE_INSURANCE_COMPANY_SUCCESS,
        insuranceCompany: response
      });

      dispatch(listInsuranceCompanies());
      return camelcaseKeys(response);
    } catch (error) {
      dispatch({
        name,
        type: CERTS_CREATE_INSURANCE_COMPANY_FAILURE,
        error
      });
    }
  };

  const createInsuranceDocumentType = docTypeData => async (dispatch, getState) => {
    dispatch({
      name,
      type: CERTS_CREATE_INSURANCE_DOCUMENT_TYPE,
      docTypeData
    });

    const { insuredId } = selectInsured(getState());

    try {
      const response = await portalApi.createAgentInsuranceDocumentType(insuredId, docTypeData);
      dispatch({
        name,
        type: CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_SUCCESS,
        response
      });

      dispatch(getDocumentTypes());
      return camelcaseKeys(response);
    } catch (error) {
      dispatch({
        name,
        type: CERTS_CREATE_INSURANCE_DOCUMENT_TYPE_FAILURE,
        error
      });
    }
  };

  const reset = () => dispatch => {
    dispatch({
      name,
      type: CERTS_RESET
    });
  };

  /* Reducer
    ========================================================= */
  const certificateReducer = (state = initialState, action) => {
    // This is where the magic happens. It allows us to use the same action types
    // across multiple similar reducers. All actions need a name attached to them.
    if (action.name !== name) return state;

    switch (action.type) {
      case CERTS_RESET: {
        return {
          ...state,
          aiScanResults: null,
          isLoading: false,
          isSubmitting: false,
          linkedDocuments: {},
          currentStep: 1,
          currentFileIndex: 0,
          currentPolicyTypeIndex: 0,
          policyData: {},
          documents: [],
          selectedPolicyTypes: [],
          additionalPolicyTypes: [],
          selectedAdditionalPolicyTypes: [],
          loadingHeader: null,
          loadingSubText: null
        };
      }

      case CERTS_SUBMIT: {
        return {
          ...state,
          isLoading: true,
          isSubmitting: true,
          loadingHeader: 'Please do not refresh or leave the page',
          loadingSubText: 'Leaving the page may result in an incomplete certificate and may require a resubmission'
        };
      }

      case CERTS_SUBMIT_SUCCESS: {
        return {
          ...state,
          isLoading: false,
          isSubmitting: false,
          loadingHeader: null,
          loadingSubText: null
        };
      }

      case CERTS_SUBMIT_FAILURE: {
        return {
          ...state,
          isLoading: false,
          isSubmitting: false,
          loadingHeader: null,
          loadingSubText: null
        };
      }

      case CERTS_PREPARE_DOCUMENT: {
        return {
          ...state,
          preparingDocument: true
        };
      }

      case CERTS_PREPARE_DOCUMENT_SUCCESS: {
        const preparedDoc = {
          preparedDocuments: action.preparedDocuments,
          original: action.file,
          type: action.file.type,
          name: action.file.name,
          size: action.file.size,
          preview: action.previewLink,
          linkedDocTypeIds: action.checkedDocTypeIds,
          linkedPolicyTypeIds: action.checkedPolicyTypeIds
        };

        return {
          ...state,
          currentFileIndex: state.documents.length,
          documents: [...state.documents, preparedDoc],
          preparingDocument: false
        };
      }

      case CERTS_PREPARE_DOCUMENT_FAILURE: {
        return {
          ...state,
          preparingDocument: false
        };
      }

      case CERTS_AI_SCAN_DOCUMENT_SUCCESS: {
        return {
          ...state,
          gptRequestId: action.response?.gptRequestId
        };
      }

      case CERTS_EXTRACT_POLICY_DATA_FROM_OPENAI_RESPONSE:
      {
        return {
          ...state,
          aiScanning: false,
          aiScanResults: action.response
        };
      }

      case CERTS_OCR_DOCUMENT: {
        return {
          ...state,
          ocr: {
            ...state.ocr,
            isPolling: true
          }
        };
      }

      case CERTS_POLL_OCR_DOCUMENT_STATUS: {
        return {
          ...state,
          ocr: {
            ...state.ocr,
            isPolling: true,
            documents: { [action.id]: { isPolling: true } }
          }
        };
      }

      case CERTS_OCR_DOCUMENT_SUCCESS:
      case CERTS_POLL_OCR_DOCUMENT_STATUS_FAILURE: {
        const modifiedDocuments = {
          ...state.ocr.documents,
          [action.id]: {
            isPolling: false,
            data: action.ocrResponse
          }
        };

        const isStillPolling = Object.values(modifiedDocuments).some(ocrObject => !!ocrObject.isPolling);

        return {
          ...state,
          ocr: {
            ...state.ocr,
            isPolling: isStillPolling,
            documents: modifiedDocuments
          }
        };
      }

      case CERTS_SET_OCR_IS_CANCELED: {
        return {
          ...state,
          ocr: {
            ...state.ocr,
            isCanceled: action.bool
          },
          aiScanning: false
        };
      }

      case CERTS_REMOVE_DOCUMENT: {
        return {
          ...state,
          documents: removeArrayItemAtIndex(state.documents, action.index)
        };
      }

      case CERTS_SET_CURRENT_STEP: {
        return {
          ...state,
          currentStep: state.currentStep + action.add
        };
      }

      case CERTS_SET_FIRST_STEP: {
        return {
          ...state,
          currentStep: action.stepNumber
        };
      }

      case CERTS_SET_COMPANY: {
        return {
          ...state,
          company: action.company
        };
      }

      case CERTS_SET_AGENCY_ID: {
        return {
          ...state,
          agencyId: action.agencyId
        };
      }

      case CERTS_SET_CURRENT_FILE: {
        return {
          ...state,
          currentFileIndex: action.index
        };
      }

      case CERTS_SET_JOB: {
        return {
          ...state,
          job: action.job
        };
      }

      case CERTS_SET_DEPARTMENT: {
        return {
          ...state,
          department: action.department
        };
      }

      case CERTS_SET_DOCUMENT_REQUIREMENTS: {
        return {
          ...state,
          documentRequirements: action.documentRequirements
        };
      }

      case CERTS_SET_LANGUAGE_REQUIREMENTS: {
        return {
          ...state,
          languageRequirements: action.languageRequirements
        };
      }

      case CERTS_SET_OPTIONS: {
        return {
          ...state,
          options: {
            ...state.options,
            ...action.options
          }
        };
      }

      case CERTS_SET_INSURED_REQUIREMENTS: {
        return {
          ...state,
          insuredRequirements: action.insuredRequirements,
          minimumRequirements: getMinimumInsuranceRequirements(
            state.itemRequirements,
            action.insuredRequirements,
            state.companyPolicyTypes
          )
        };
      }

      case CERTS_SET_INSURED: {
        return {
          ...state,
          insured: action.insured
        };
      }

      case CERTS_SET_ITEM_REQUIREMENTS: {
        return {
          ...state,
          itemRequirements: action.itemRequirements,
          minimumRequirements: getMinimumInsuranceRequirements(
            action.itemRequirements,
            state.insuredRequirements,
            state.companyPolicyTypes
          )
        };
      }

      case CERTS_SET_USER: {
        return {
          ...state,
          user: action.user
        };
      }

      case CERTS_SET_POLICY_REVIEW_CONTENT_HEIGHT: {
        return {
          ...state,
          policyReviewContentHeight: action.height
        };
      }

      case CERTS_SET_INSURANCE_POLICY_TYPES: {
        return {
          ...state,
          companyPolicyTypes: action.companyPolicyTypes,
          currentPolicyTypeIndex: 0
        };
      }

      case CERTS_SET_REQUIRED_POLICY_TYPES: {
        return {
          ...state,
          requirementPolicyTypes: action.requirementPolicyTypes,
          additionalPolicyTypes: action.additionalPolicyTypes
        };
      }

      case CERTS_ADD_ADDITIONAL_INSURANCE_POLICY_TYPE: {
        const filteredTypes = state.selectedAdditionalPolicyTypes.filter(type => type.id !== action.policyType.id);

        return {
          ...state,
          selectedAdditionalPolicyTypes: [
            ...filteredTypes,
            action.policyType
          ]
        };
      }

      case CERTS_REMOVE_ADDITIONAL_INSURANCE_POLICY_TYPE: {
        const filteredPolicyTypes = removeArrayItemAt(state.selectedAdditionalPolicyTypes, 'id', action.policyType.id);
        return {
          ...state,
          selectedAdditionalPolicyTypes: filteredPolicyTypes
        };
      }

      case CERTS_ADD_SELECTED_INSURANCE_POLICY_TYPE: {
        return {
          ...state,
          selectedPolicyTypes: [
            ...state.selectedPolicyTypes,
            action.policyType
          ]
        };
      }

      case CERTS_REMOVE_SELECTED_INSURANCE_POLICY_TYPE: {
        const filteredPolicyTypes = removeArrayItemAt(state.selectedPolicyTypes, 'id', action.policyTypeId);
        return {
          ...state,
          selectedPolicyTypes: filteredPolicyTypes
        };
      }

      case CERTS_NEXT_POLICY_TYPE: {
        return {
          ...state,
          currentPolicyTypeIndex: state.currentPolicyTypeIndex + 1
        };
      }

      case CERTS_SKIP_POLICY_TYPE: {
        return {
          ...state,
          policyData: {
            ...state.policyData,
            [action.policyTypeId]: {
              ...state.policyData[action.policyTypeId],
              skipped: true
            }
          }
        };
      }

      case CERTS_PREVIOUS_POLICY_TYPE: {
        return {
          ...state,
          currentPolicyTypeIndex: state.currentPolicyTypeIndex - 1
        };
      }

      case CERTS_SET_POLICY_DATA: {
        return {
          ...state,
          policyData: {
            ...state.policyData,
            [action.policyTypeId]: {
              ...action.data,
              skipped: false
            }
          }
        };
      }

      case CERTS_LIST_INSURANCE_COMPANIES_SUCCESS: {
        const receivedData = action.pageInfo.currentPage !== 1
          ? [...state.insuranceCompanies.data, ...action.data]
          : action.data;

        return {
          ...state,
          insuranceCompanies: {
            data: receivedData,
            pageInfo: action.pageInfo
          }
        };
      }

      case CERTS_GET_DOCUMENT_TYPES_SUCCESS: {
        return {
          ...state,
          insuranceDocumentTypes: action.data
        };
      }

      case CERTS_GET_USER_DOCUMENTS: {
        return {
          ...state,
          documentsLoading: true
        };
      }

      case CERTS_GET_USER_DOCUMENTS_SUCCESS: {
        return {
          ...state,
          userDocuments: action.userDocuments,
          documentsLoading: false
        };
      }

      case CERTS_GET_USER_DOCUMENTS_FAILURE: {
        return {
          ...state,
          documentsLoading: false
        };
      }

      case CERTS_GET_USER_POLICIES: {
        return {
          ...state,
          policiesLoading: true
        };
      }

      case CERTS_GET_USER_POLICIES_SUCCESS: {
        return {
          ...state,
          userPolicies: action.userPoliciesWithLoaders,
          policiesLoading: false
        };
      }

      case CERTS_GET_USER_POLICIES_FAILURE: {
        return {
          ...state,
          policiesLoading: false
        };
      }

      case CERTS_UPDATE_POLICY: {
        const modifiedPolicies = modifyArrayItemAt(
          state.userPolicies,
          'id',
          action.policyId,
          policy => ({
            ...policy,
            isLoading: true
          })
        );

        return {
          ...state,
          userPolicies: modifiedPolicies
        };
      }

      case CERTS_UPDATE_POLICY_SUCCESS: {
        const modifiedPolicies = modifyArrayItemAt(
          state.userPolicies,
          'id',
          action.response.id,
          policy => ({
            ...policy,
            ...action.response,
            isLoading: false
          })
        );

        return {
          ...state,
          userPolicies: modifiedPolicies
        };
      }

      case CERTS_UPDATE_POLICY_FAILURE: {
        const modifiedPolicies = modifyArrayItemAt(
          state.userPolicies,
          'id',
          action.policyId,
          policy => ({
            ...policy,
            isLoading: false
          })
        );

        return {
          ...state,
          userPolicies: modifiedPolicies
        };
      }

      case CERTS_SET_INSURANCE_REQUIREMENT:
        return {
          ...state,
          insuranceRequirement: action.insuranceRequirement
        };

      default:
        return state;
    }
  };

  const reducer = baseReducer
    ? combineReducers({
      base: baseReducer || (({ state }) => state),
      certs: certificateReducer
    })
    : certificateReducer;

  /* Selectors
  ========================================================= */
  const selectAgencyId = state => state[name].agencyId;
  const selectLinkedDocuments = state => state[name].linkedDocuments;
  const selectAllDocuments = state => state[name].documents;
  const selectContentHeight = state => state[name].contentHeight;

  const selectCurrentFile = state => (state[name].currentFileIndex >= 0
    ? state[name].documents[state[name].currentFileIndex]
    : state[name].documents.find(d => d.linkedPolicyTypeIds.includes(selectCurrenPolicyType(state).id)));

  const selectCurrenPolicyType = state => (selectOptions(state).chooseAnyPolicyType
    ? selectCurrentPolicyTypeFromSelected(state)
    : selectCurrentPolicyTypeFromRequirements(state));

  const selectCurrentPolicyTypeFromRequirements = state => {
    const policyTypes = [...state[name].requirementPolicyTypes, ...state[name].selectedAdditionalPolicyTypes];

    return policyTypes[state[name].currentPolicyTypeIndex];
  };

  const selectCurrentPolicyTypeFromSelected = state => state[name].selectedPolicyTypes[state[name].currentPolicyTypeIndex];
  const selectCurrentStep = state => state[name].currentStep;
  const selectJob = state => state[name].job;
  const selectDepartment = state => state[name].department;
  const selectCompany = state => state[name].company;
  const selectDocumentRequirements = state => state[name].documentRequirements;
  const selectUserDocuments = state => state[name].userDocuments;
  const selectUserPolicies = state => state[name].userPolicies;
  const selectLanguageRequirements = state => state[name].languageRequirements;
  const selectOptions = state => state[name].options;
  const selectInsuredRequirements = state => state[name].insuredRequirements;
  const selectInsured = state => state[name].insured;
  const selectItemRequirements = state => state[name].itemRequirements;
  const selectRequirementPolicyTypes = state => state[name].requirementPolicyTypes;
  const selectAdditionalPolicyTypes = state => state[name].additionalPolicyTypes;
  const selectSelectedAdditionalPolicyTypes = state => state[name].selectedAdditionalPolicyTypes;
  const selectCompanyPolicyTypes = state => state[name].companyPolicyTypes;
  const selectInsuranceDocumentTypes = state => state[name].insuranceDocumentTypes;
  const selectSelectedPolicyTypes = state => state[name].selectedPolicyTypes;
  const selectPolicyData = state => state[name].policyData;
  const selectInsuranceCompanies = state => state[name].insuranceCompanies.data;
  const selectInsuranceCompanyPageInfo = state => state[name].insuranceCompanies.pageInfo;
  const selectSubmissionText = state => state[name].submissionText;
  const selectUser = state => state[name].user;
  const selectIsLoading = state => state[name].isLoading;
  const selectIsSubmitting = state => state[name].isSubmitting;
  const selectPoliciesLoading = state => state[name].policiesLoading;
  const selectDocumentsLoading = state => state[name].documentsLoading;
  const selectPolicyReviewContentHeight = state => state[name].policyReviewContentHeight;
  const selectInsuranceRequirement = state => state[name].insuranceRequirement;

  /**
   * Mirrors how the API picks the submission job: documents are scanned for expiration dates only
   * when the company has AI document analysis, the record has no policy requirements, and at least
   * one document was prepared.
   */
  const selectWillScanDocuments = state => {
    const hasPolicyRequirements = selectRequirementPolicyTypes(state).length > 0;
    const hasDocuments = selectAllDocuments(state).some(doc => doc?.preparedDocuments?.length);

    return !hasPolicyRequirements && hasDocuments;
  };

  const selectIsInitializing = state => {
    const job = selectJob(state);
    const company = selectCompany(state);
    const documentRequirements = selectDocumentRequirements(state);
    const insuredRequirements = selectInsuredRequirements(state);
    const itemRequirements = selectItemRequirements(state);
    const policyTypes = selectRequirementPolicyTypes(state);

    return [job, company, documentRequirements, insuredRequirements, itemRequirements, policyTypes]
      .some(dep => !dep);
  };

  const selectMissingDocumentTypeIds = state => {
    const allLinkedDocTypeIds = selectAllDocuments(state)
      .reduce((ids, document) => [...ids, ...document.linkedDocTypeIds], []);

    return selectDocumentRequirements(state)
      .filter(({ insuranceDocumentTypeId }) => !allLinkedDocTypeIds.includes(insuranceDocumentTypeId))
      .map(i => i.insuranceDocumentTypeId);
  };

  const selectMissingPolicyTypeIds = state => {
    const allLinkedPolicyTypes = selectAllDocuments(state)
      .reduce((ids, document) => [...ids, ...document.linkedPolicyTypeIds], []);

    return Object.values(selectMinimumRequirements(state))
      .filter(({ policyTypeId }) => !allLinkedPolicyTypes.includes(policyTypeId))
      .map(({ policyTypeId }) => policyTypeId);
  };

  const selectMinimumRequirements = state => state[name].minimumRequirements;

  const selectOcrStillPolling = state => state[name].ocr.isPolling || Object.values(state[name].ocr.documents).some(ocrObject => !!ocrObject.isPolling);

  const selectOcrIsCanceled = state => state[name].ocr.isCanceled;

  const selectLoadingHeaderText = state => state[name].loadingHeader;
  const selectLoadingSubText = state => state[name].loadingSubText;

  const selectPreparingDocument = state => state[name].preparingDocument;

  const selectGptRequestId = state => state[name].gptRequestId;

  const selectPolicyGptRequestId = state => state[name].policyGptRequestId;

  const selectAiScanning = state => state[name].aiScanning;
  const selectAiScanResults = state => state[name].aiScanResults;

  return {
    addSelectedInsurancePolicyType,
    createInsuranceCompany,
    createInsuranceDocumentType,
    deleteDocument,
    getUserDocuments,
    getUserPolicies,
    goBack,
    initialState,
    listInsuranceCompanies,
    getDocumentTypes,
    nextPolicyType,
    nextStep,
    prepareDocument,
    aiScanDocuments,
    previousPolicyType,
    reducer,
    removeDocument,
    removeSelectedInsurancePolicyType,
    reset,
    selectAgencyId,
    selectAdditionalPolicyTypes,
    selectAllDocuments,
    selectCompany,
    selectCompanyPolicyTypes,
    selectInsuranceDocumentTypes,
    selectContentHeight,
    selectCurrentFile,
    selectCurrentPolicyTypeFromRequirements,
    selectCurrentPolicyTypeFromSelected,
    selectCurrentStep,
    selectDocumentsLoading,
    selectDocumentRequirements,
    selectInsuranceCompanies,
    selectInsuranceCompanyPageInfo,
    selectInsured,
    selectInsuredRequirements,
    selectIsInitializing,
    selectIsLoading,
    selectIsSubmitting,
    selectWillScanDocuments,
    selectLoadingHeaderText,
    selectLoadingSubText,
    selectItemRequirements,
    selectJob,
    selectDepartment,
    selectGptRequestId,
    selectPolicyGptRequestId,
    selectAiScanning,
    selectAiScanResults,
    selectLanguageRequirements,
    selectLinkedDocuments,
    selectMinimumRequirements,
    selectMissingDocumentTypeIds,
    selectMissingPolicyTypeIds,
    selectOcrStillPolling,
    selectOcrIsCanceled,
    selectOptions,
    selectPolicyData,
    selectPoliciesLoading,
    selectPolicyReviewContentHeight,
    selectPreparingDocument,
    selectRequirementPolicyTypes,
    selectSelectedAdditionalPolicyTypes,
    selectSelectedPolicyTypes,
    selectSubmissionText,
    selectUser,
    selectUserDocuments,
    selectUserPolicies,
    setAgencyId,
    setCompany,
    setCurrentFile,
    setDocumentRequirements,
    setFirstStep,
    setInsurancePolicyTypes,
    setRequiredPolicyTypes,
    setInsured,
    setInsuredRequirements,
    setItemRequirements,
    setJob,
    setDepartment,
    setLanguageRequirements,
    setOcrIsCanceled,
    setOptions,
    setPolicyData,
    setUser,
    setPolicyReviewContentHeight,
    skipPolicyType,
    submit,
    updatePolicy,
    setInsuranceRequirement,
    selectInsuranceRequirement,
    addAdditionalInsurancePolicyType,
    removeAdditionalInsurancePolicyType,
    extractPolicyDataFromOpenAiResponse
  };
};
